import React from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { DateTime } from 'luxon';

import { aggErrors } from 'app-components/flux/account-discovery';
import { accountsUtils } from 'app-components/flux/accounts';
import { billPresentmentActions, billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import {
  AutoAdjustAmountEnum,
  mkScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker, getLogger } from 'app-components/utils/core';

const log = getLogger('BillConnectTab');

import InfoIcon from '@mui/icons-material/Info';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import MenuButton from '@mui/icons-material/MoreVert';
import ReconnectIcon from '@mui/icons-material/RestartAlt';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { makeStyles } from 'tss-react/mui';

import ConnectedAvatar from 'components/ConnectedAvatar';
import { InitView, PendingView } from 'components/Dialogs/BillPresentment/HoldingScreen';
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import { getBillPresentmentRootModalDrawerOptions } from 'components/Dialogs/BillPresentment/drawerOptions';
import { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import Dump from 'components/Dump';
import QSwitch from 'components/QSwitch';

import { useScheduledTransactionEditFormAccessors } from '../hooks/useScheduledTransactionEditFormFields';
import type { ScheduledTransactionEditFormAccessors } from '../types';

const useStyles = makeStyles()((theme) => ({
  connectBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    padding: 16,
    marginBottom: 24,
    gap: 8,
  },
  accountBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 16,
    backgroundColor: theme.palette.info.light,
    borderRadius: 12,
    gap: 8,
    '&.error': {
      backgroundColor: theme.palette.error.light,
    },
  },
  radioSection: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 32px 0 32px',
  },
  billField: {
    marginTop: 8,
    marginLeft: 12,
  },
  switch: {
    margin: 0,
    marginLeft: 16,
  },
  // Added classes for sx props in the selection
  billConnectCard: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: theme.palette.info.light,
    '&.error': {
      backgroundColor: theme.palette.error.light,
    },
  },
  billConnectCardLeft: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  billConnectCardAvatarBox: {
    margin: theme.spacing(2),
  },
  billConnectCardRight: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  menuUnlink: {
    color: theme.palette.error.main,
  },
  menuUnlinkIcon: {
    color: theme.palette.error.main,
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: 4,
    height: 20,
    width: 20,
  },
  billConnectInfoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const smallRadio = <Radio sx={{ padding: '6px', marginLeft: '3px' }} />;
const labelSlot = { typography: { variant: 'paragraphRegular' } } as any;

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

interface BillConnectTabProps {
  form: ScheduledTransactionEditFormAccessors;
  rootModalDrawer?: RootModalDrawerOptions;
}

const tooltip = (
  <>
    <Typography variant='paragraphRegular' gutterBottom>
      Until your balance is finalized, Simplifi will calculate your amount due by adding your credit card transactions
      between your statement closing date and due date to provide a more accurate cash projection.
    </Typography>
    <Typography variant='paragraphRegular'>
      We&apos;ll estimate your statement closing date as three days before your due date if it&apos;s not provided by
      your financial institution.
    </Typography>
  </>
);

function BillConnectTab({ form: formAccessors, rootModalDrawer }: BillConnectTabProps) {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const form = useScheduledTransactionEditFormAccessors(formAccessors);
  const isInDrawer = useStandardDrawerVariant() !== null;
  const billPresentmentRootModalDrawer = getBillPresentmentRootModalDrawerOptions({
    isInDrawer,
    rootModalDrawer,
  });

  const presentmentAccountId = form.values?.presentmentAccountId || '';

  const billerAccount = useAppSelector((state) =>
    billPresentmentSelectors.selectBillerAccountById(state, presentmentAccountId),
  );
  const biller = useAppSelector(
    (state) => billerAccount && billPresentmentSelectors.selectBillerById(state, billerAccount.billerId),
  );

  const { transaction } = (form?.values || {}) as { transaction: TransactionProps };
  const { autoAdjustAmount, autoAdjustDueOn } = form.values || {};

  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, transaction));
  const institutionLogin = useAppSelector((state) =>
    creditAccount?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, creditAccount.institutionLoginId)
      : undefined,
  );

  const model = mkScheduledTransaction({
    ...(form.values || {}),
    dueOn: form.values?.dueOn?.toISODate(),
  } as any);

  const BVA = useAppSelector((state) => billPresentmentSelectors.getCanBVA(state, model));

  const allDisabled = Boolean(
    (!autoAdjustAmount || autoAdjustAmount === AutoAdjustAmountEnum.none) && !autoAdjustDueOn,
  );
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const statementDueAt = creditAccount?.statementDueAt;
  const statementDueDate = creditAccount?.statementDueDate;
  const dueDate = statementDueAt
    ? statementDueAt.endsWith('T00:00:00Z')
      ? DateTime.fromISO(statementDueAt, { zone: 'utc' })
      : DateTime.fromISO(statementDueAt)
    : statementDueDate
      ? DateTime.fromISO(statementDueDate)
      : null;
  const dueDay = dueDate?.isValid ? `${ordinal(dueDate.day)} day of the month` : ' - ';
  const closeDate = creditAccount && DateTime.fromISO(creditAccount.statementCloseAt || '');
  const closeDay = closeDate?.isValid ? `${ordinal(closeDate.day)} day of the month` : ' - ';

  if (!BVA && (!presentmentAccountId || !billerAccount)) {
    return <InitView model={model} rootModalDrawer={rootModalDrawer} />;
  }
  if (!BVA && billerAccount && model.billerEnrollmentStatus === 'PENDING') {
    return <PendingView />;
  }

  const accountError = accountsUtils.isAccountInError(creditAccount)?.isError;
  const billerError = Boolean(
    billerAccount && (model?.billerEnrollmentStatus === 'FAILED' || billerAccount?.status === 'FAILED'),
  );

  const handleReconnect = () => {
    const aggregation = creditAccount?.aggregators?.get(0);
    const aggAction = aggErrors.getQcsAggregationAction(
      institutionLogin,
      aggregation?.aggStatus,
      aggregation?.aggStatusCode,
      featureFlagsSelectors.getFeatureFlags(store.getState()),
    );
    if (aggAction) {
      aggErrors.handleAggAction(dispatch, aggAction, 'BVA connection status');
    } else {
      log.error(
        'No aggAction available for BVA Reconnect button',
        '\nInstitution Login ID:',
        institutionLogin?.id,
        '\nInstitution:',
        institutionLogin?.name,
        '\nAgg Status:',
        aggregation?.aggStatus,
        '\nAgg Status Code:',
        aggregation?.aggStatusCode,
      );
    }
  };

  const handleUnlinkAccount = () => {
    const payee = transaction?.payee;
    showConfirmationDialog({
      id: 'bva-unlink-confirmation',
      size: 'sm',
      title: 'Disconnect account',
      content: `Are you sure you want to disconnect this account from ${payee ? `your ${payee} series` : 'this series'}? Your reminders will no longer update with your latest bill information.\n\nNote: this account will remain connected to Simplifi.`,
      confirmLabel: 'Disconnect',
      confirmButtonVariant: 'delete',
      confirmAction: () => {
        form.setters.setFieldValue('autoAdjustAmount', AutoAdjustAmountEnum.none);
        form.setters.setFieldValue('autoAdjustDueOn', false);
        tracker.track(tracker.events.disconnectBiller, { initiator: 'bill connect tab', type: 'BVA' });
      },
      denyLabel: 'Cancel',
      denyButtonVariant: 'outlined',
    });
  };

  return (
    <>
      <div className={classes.connectBox}>
        <Stack flexDirection='row'>
          <Typography variant='bodyMedium'>Bill Connect</Typography>

          {BVA && (
            <QSwitch
              id='disable-bill-connect-switch'
              checked={!allDisabled}
              onChange={() => {
                if (allDisabled) {
                  form.setters.setFieldValue('autoAdjustAmount', AutoAdjustAmountEnum.statement);
                } else {
                  form.setters.setFieldValue('autoAdjustAmount', AutoAdjustAmountEnum.none);
                  form.setters.setFieldValue('autoAdjustDueOn', false);
                }
              }}
              className={classes.switch}
            />
          )}
        </Stack>
        {BVA ? (
          <Typography variant='paragraphRegular' color='textSecondary'>
            Automatically update the amount and due date of your reminders by connecting this series to the matching
            connected account in Simplifi.
          </Typography>
        ) : (
          <Typography variant='paragraphRegular' color='textSecondary'>
            Automatically update the amount and due date of your Reminders with your actual bill data.
          </Typography>
        )}

        {BVA ? (
          <div
            className={cx(classes.accountBox, accountError ? 'error' : '')}
            style={{ opacity: allDisabled ? 0.4 : 1 }}
          >
            <Stack flexDirection='row' width='100%' alignItems='center' mb={accountError ? 0 : 2}>
              <Dump obj={creditAccount} />

              <ConnectedAvatar kind='bill' txn={transaction as TransactionProps} />

              <Stack ml={2} mr='auto'>
                <Typography variant='captionRegular' color='textSecondary'>
                  Connected account
                </Typography>
                <Typography variant='bodyMedium'>
                  {`${creditAccount?.name || ' - No credit account found - '} ${accountsUtils?.formatFinalFour(creditAccount)}`}
                </Typography>
              </Stack>

              {accountError ? (
                <Button id='reconnect-btn' onClick={handleReconnect}>
                  Reconnect
                </Button>
              ) : null}

              <IconButton
                id='bva-menu-button'
                aria-label='edit-account-connection'
                size='medium'
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
                focusRipple
              >
                <MenuButton />
              </IconButton>
              {menuAnchor && (
                <Menu
                  id='bva-bill-connect-menu'
                  anchorEl={menuAnchor}
                  keepMounted
                  open
                  onClose={() => setMenuAnchor(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {accountError ? (
                    <MenuItem
                      id='bva-menu-reconnect'
                      onClick={() => {
                        setMenuAnchor(null);
                        handleReconnect();
                      }}
                    >
                      <ListItemIcon>
                        <ReconnectIcon />
                      </ListItemIcon>
                      Reconnect account
                    </MenuItem>
                  ) : null}
                  {accountError ? <Divider /> : null}
                  <MenuItem
                    id='bva-menu-unlink'
                    className={classes.menuUnlink}
                    onClick={() => {
                      setMenuAnchor(null);
                      handleUnlinkAccount();
                    }}
                  >
                    <ListItemIcon className={classes.menuUnlinkIcon}>
                      <UnlinkIcon />
                    </ListItemIcon>
                    Unlink account
                  </MenuItem>
                </Menu>
              )}
            </Stack>

            {!accountError ? (
              <>
                <div className={classes.billConnectInfoContainer}>
                  <Typography
                    variant='paragraphRegular'
                    color='textSecondary'
                  >{`Set this account’s statement dates for accurate cash flow projection.`}</Typography>
                  <Tooltip arrow placement='right' title={tooltip}>
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </div>

                <Stack flexDirection='row' width='100%'>
                  <TextField
                    label='Statement closing date'
                    value={closeDay}
                    variant='standard'
                    fullWidth
                    className={classes.billField}
                    slotProps={{
                      input: { readOnly: true, disableUnderline: true },
                    }}
                  />

                  <TextField
                    label='Due date'
                    value={dueDay}
                    variant='standard'
                    fullWidth
                    className={classes.billField}
                    slotProps={{
                      input: { readOnly: true, disableUnderline: true },
                    }}
                  />
                </Stack>
              </>
            ) : null}
          </div>
        ) : (
          <Box className={cx(classes.billConnectCard, billerError ? 'error' : '')}>
            <Dump obj={biller} />
            <Box className={classes.billConnectCardLeft}>
              <Box className={classes.billConnectCardAvatarBox}>
                <ConnectedAvatar kind='bill' txn={transaction as TransactionProps} model={model} />
              </Box>
              <Box>
                <Typography variant='paragraphMedium' color='textSecondary'>
                  {biller?.name || 'TEST ACCOUNT'}
                </Typography>
                <Typography variant='captionRegular'>{biller?.logoKey || billerAccount?.id}</Typography>
              </Box>
            </Box>
            <Box className={classes.billConnectCardRight}>
              <Dump obj={billerAccount} />
              {billerError ? (
                <Button
                  id='biller-fix-button'
                  onClick={() => {
                    dispatch(doBillPresentment(model, location, billPresentmentRootModalDrawer));
                    tracker.track(tracker.events.fixBillerStart, { initiator: location });
                  }}
                >
                  Reconnect
                </Button>
              ) : null}
              <IconButton
                id='biller-menu-button'
                aria-label='edit-biller-status'
                size='medium'
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
                focusRipple
              >
                <MenuButton />
              </IconButton>
              {menuAnchor && (
                <Menu
                  id='bill-connect-menu'
                  anchorEl={menuAnchor}
                  keepMounted
                  open
                  onClose={() => setMenuAnchor(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    id='menu-unlink'
                    className={classes.menuUnlink}
                    onClick={() => {
                      dispatch(billPresentmentActions.disconnect({ model }));
                      form.setters.setFieldValue('presentmentAccountId', null);
                      form.setters.setFieldValue('billerEnrollmentStatus', null);
                    }}
                  >
                    <ListItemIcon className={classes.menuUnlinkIcon}>
                      <UnlinkIcon />
                    </ListItemIcon>
                    Disconnect biller
                  </MenuItem>
                </Menu>
              )}
            </Box>
          </Box>
        )}
      </div>

      <Typography variant='paragraphRegular' color='textSecondary'>
        When your statement period ends, update reminder to:
      </Typography>

      <div className={classes.radioSection} style={{ opacity: BVA && allDisabled ? 0.4 : 1 }}>
        <FormControl fullWidth>
          <Typography variant='paragraphMedium' id='bill-amount-label'>
            Amount
          </Typography>
          <RadioGroup
            aria-labelledby='bill-amount-label'
            defaultValue={AutoAdjustAmountEnum.statement}
            name='bill-amount-group'
            value={autoAdjustAmount}
            onChange={(_, value) => {
              form.setters.setFieldValue('autoAdjustAmount', value);
            }}
          >
            <FormControlLabel
              value={AutoAdjustAmountEnum.statement}
              label='Statement balance'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.minimum}
              label='Minimum due'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.total}
              label='Total Balance'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.none}
              label="Don't update (use series amount)"
              control={smallRadio}
              slotProps={labelSlot}
            />
          </RadioGroup>
        </FormControl>

        <FormControl fullWidth>
          <Typography variant='paragraphMedium' id='bill-date-label'>
            Date
          </Typography>
          <RadioGroup
            aria-labelledby='bill-date-label'
            defaultValue='false'
            name='bill-date-group'
            value={String(autoAdjustDueOn)}
            onChange={(_, value) => {
              form.setters.setFieldValue('autoAdjustDueOn', value === 'true' ? true : false);
            }}
          >
            <FormControlLabel value='true' label='Due Date' control={smallRadio} slotProps={labelSlot} />
            <FormControlLabel
              value='false'
              label="Don't update (use series date)"
              control={smallRadio}
              slotProps={labelSlot}
            />
          </RadioGroup>
        </FormControl>
      </div>
    </>
  );
}

BillConnectTab.displayName = 'BillConnectTab';

export default BillConnectTab;
